import type { AccessCatalogItemPublic } from '@accounts/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';

import { STR } from '../../i18n';
import {
  ApiError,
  createMyAccessRequest,
  createMyOtherRequest,
  listMyAccessRequests,
  listMyApplications,
  listMyRequestableCatalog,
} from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import { showToast } from '../../lib/toast';
import { Modal } from '../../ui/Modal';
import { Callout } from '../../ui/Primitives';
import {
  checkboxStyle,
  fieldLabelStyle,
  modalButtonStyle,
  quietButtonStyle,
  rowActionStyle,
  searchFieldStyle,
  textFieldStyle,
} from '../../ui/styles';
import { pathLabel } from '../admin/access/accessFormat';

/**
 * REQUEST ACCESS — the FINAL demo's two-step picker (spec row 3; board pick R3-2, launcher
 * option e2): step 1 picks a GROUP (the hierarchy's top level, plus "Suite applications" for
 * rows bound to an app and "Not filed" for rows with no place), step 2 ticks its systems.
 * Picks are kept in ONE set across groups, so going back to "All groups" and adding from
 * another keeps everything already ticked. Typing in the search drops the groups and lists
 * every matching row at once.
 *
 * Only live, requestable rows ever arrive (`/api/me/access/catalog` filters server-side).
 * What the person already has is greyed "You have it" and what they already asked for is
 * greyed "Requested". A suite app is "had" when it is on their launcher (the launcher read,
 * by application id). An OUTSIDE system cannot be marked as had here: there is no self-read
 * of one's own access records, so only a pending request greys it — and the server refuses
 * a duplicate with its own message, which the send reports.
 *
 * SOMETHING ELSE (spec row 13): the LAST tile of step 1, a link on step 2 and under a search,
 * opens a short form — what they need, where it belongs (optional), why. It rides in the same
 * send as the ticked rows and files an Other request that only administrators see.
 *
 * WHY (FINAL demo `reqReason`): one optional short reason under the picks, sent as `note` on
 * every ticked row's request so the approver sees it in the Queue. Blank sends no `note` at
 * all; the server trims and caps it at 2000 characters, so the box does too. The Something
 * else form asks its own why, so the box steps aside while that form is open.
 */

const SUITE_GROUP = 'suite';
const NOT_FILED_GROUP = 'not-filed';
/** Step 1's last tile: not a group of rows but the Something else form. */
const OTHER_GROUP = 'other';

/** The Something else form. `whereNodeId` is a top-level place from the person's own catalog. */
export interface OtherDraft {
  what: string;
  whereNodeId: string | null;
  why: string;
}

const EMPTY_OTHER: OtherDraft = { what: '', whereNodeId: null, why: '' };

/** The server's cap on a request note (`createRequestInputSchema`). */
const REQUEST_NOTE_MAX = 2000;

interface Group {
  id: string;
  name: string;
  rows: AccessCatalogItemPublic[];
}

type RowState = 'has' | 'pending' | null;

function groupIdOf(item: AccessCatalogItemPublic): string {
  if (item.application !== null) return SUITE_GROUP;
  return item.path[0]?.id ?? NOT_FILED_GROUP;
}

/** Suite applications first, the hierarchy's top-level entries by name, "Not filed" last. */
function groupRows(rows: readonly AccessCatalogItemPublic[]): Group[] {
  const byId = new Map<string, Group>();
  for (const item of rows) {
    const id = groupIdOf(item);
    let group = byId.get(id);
    if (group === undefined) {
      const name =
        id === SUITE_GROUP
          ? STR.access.request.suiteGroup
          : id === NOT_FILED_GROUP
            ? STR.accessCommon.notPlaced
            : (item.path[0]?.name ?? STR.accessCommon.notPlaced);
      group = { id, name, rows: [] };
      byId.set(id, group);
    }
    group.rows.push(item);
  }
  const rank = (group: Group) => (group.id === SUITE_GROUP ? 0 : group.id === NOT_FILED_GROUP ? 2 : 1);
  return [...byId.values()]
    .map((group) => ({ ...group, rows: [...group.rows].sort((a, b) => a.name.localeCompare(b.name)) }))
    .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

/** Where the row sits below its group: the rest of its path, then its own name. */
function rowName(item: AccessCatalogItemPublic): string {
  if (item.application !== null) return item.name;
  const below = item.path.slice(1);
  return below.length === 0 ? item.name : `${pathLabel(below)}${STR.accessCommon.pathSeparator}${item.name}`;
}

export function RequestAccessDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [picks, setPicks] = useState<ReadonlySet<string>>(() => new Set());
  const [groupId, setGroupId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [other, setOther] = useState<OtherDraft>(EMPTY_OTHER);
  const [reason, setReason] = useState('');

  const catalogQuery = useQuery({
    queryKey: queryKeys.myAccessCatalog,
    queryFn: listMyRequestableCatalog,
  });
  const requestsQuery = useQuery({
    queryKey: queryKeys.myAccessRequests,
    queryFn: listMyAccessRequests,
  });
  const applicationsQuery = useQuery({
    queryKey: queryKeys.myApplications,
    queryFn: listMyApplications,
  });

  const catalog = catalogQuery.data?.data ?? [];
  const heldApps = new Set((applicationsQuery.data?.data ?? []).map((app) => app.id));
  const pendingItems = new Set(
    (requestsQuery.data?.data ?? [])
      .filter((request) => request.status === 'pending')
      .map((request) => request.catalogItem.id),
  );

  function stateOf(item: AccessCatalogItemPublic): RowState {
    if (item.application !== null && heldApps.has(item.application.id)) return 'has';
    if (pendingItems.has(item.id)) return 'pending';
    return null;
  }

  const groups = groupRows(catalog);
  const onOther = groupId === OTHER_GROUP;
  const group = groupId === null || onOther ? null : (groups.find((entry) => entry.id === groupId) ?? null);
  // "Where does it belong?" offers the top-level places the person's own catalog shows.
  const places = [
    ...new Map(
      catalog.flatMap((item) => (item.path[0] === undefined ? [] : [[item.path[0].id, item.path[0].name] as const])),
    ),
  ].sort((a, b) => a[1].localeCompare(b[1]));
  const otherWhat = other.what.trim();
  const hasOther = otherWhat !== '';
  // The spec asks what AND why; only "where" is optional. A started form without a why holds
  // the whole send back rather than dropping the Other request silently.
  const otherIncomplete = hasOther && other.why.trim() === '';
  const query = search.trim().toLowerCase();
  const matches =
    query === ''
      ? []
      : catalog.filter((item) =>
          `${item.name} ${pathLabel(item.path)} ${item.application?.name ?? ''}`
            .toLowerCase()
            .includes(query),
        );

  // A pick that has since become "had" or "requested" (a refetch) no longer counts.
  const pickedItems = catalog.filter((item) => picks.has(item.id) && stateOf(item) === null);

  function toggle(id: string) {
    setPicks((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const sendMutation = useMutation({
    mutationFn: ({
      ids,
      note,
      otherDraft,
    }: {
      ids: readonly string[];
      note: string | undefined;
      otherDraft: OtherDraft | null;
    }) =>
      Promise.allSettled([
        ...ids.map((id) => createMyAccessRequest(id, note)),
        ...(otherDraft === null
          ? []
          : [
              createMyOtherRequest({
                what: otherDraft.what.trim(),
                nodeId: otherDraft.whereNodeId,
                why: otherDraft.why.trim(),
              }),
            ]),
      ]),
    onSuccess: (results) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.myAccessRequests });
      void queryClient.invalidateQueries({ queryKey: queryKeys.myAccessCatalog });
      void queryClient.invalidateQueries({ queryKey: queryKeys.myOtherRequests });
      const sent = results.filter((result) => result.status === 'fulfilled').length;
      const failures = results.filter(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      );
      if (failures.length === 0) {
        showToast(sent === 1 ? STR.access.request.sentOne : STR.access.request.sentMany(sent));
        onClose();
        return;
      }
      const first: unknown = failures[0]?.reason;
      const reason = first instanceof ApiError ? first.message : STR.errors.generic;
      if (sent === 0) {
        showToast(reason, 'warn');
        return;
      }
      showToast(STR.access.request.sentPartly(sent, failures.length, reason), 'warn');
      onClose();
    },
  });

  const lead = onOther
    ? STR.otherRequests.stepLead
    : query !== ''
      ? STR.access.request.leadSearch
      : group === null
        ? STR.access.request.leadStep1
        : STR.access.request.leadStep2;

  const otherLink = (
    <button
      type="button"
      className="tbtn"
      style={{ ...rowActionStyle, alignSelf: 'flex-start', paddingLeft: 0, color: 'var(--accent)' }}
      onClick={() => {
        setSearch('');
        setGroupId(OTHER_GROUP);
      }}
    >
      {STR.otherRequests.link}
    </button>
  );

  function rowList(rows: readonly AccessCatalogItemPublic[]): ReactNode {
    if (rows.length === 0) {
      return (
        <div className="tx-subtle" style={{ padding: '18px 12px', fontSize: 12.5, textAlign: 'center' }}>
          {STR.access.request.noMatch}
        </div>
      );
    }
    return rows.map((item) => {
      const state = stateOf(item);
      const off = state !== null;
      return (
        <label
          key={item.id}
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            padding: '9px 12px',
            borderBottom: '1px solid var(--border)',
            fontSize: 12.5,
            cursor: off ? 'default' : 'pointer',
            opacity: off ? 0.6 : 1,
          }}
        >
          <input
            type="checkbox"
            style={{ ...checkboxStyle, margin: 0 }}
            checked={!off && picks.has(item.id)}
            disabled={off}
            onChange={() => {
              toggle(item.id);
            }}
          />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ fontWeight: 600 }}>{query === '' ? rowName(item) : item.name}</span>{' '}
            <span className="tx-subtle">
              {item.application !== null
                ? STR.access.request.suiteHint
                : query === ''
                  ? STR.access.request.outsideHint
                  : pathLabel(item.path)}
            </span>
          </span>
          {state === null ? null : (
            <span className="tx-subtle" style={{ fontSize: 11.5, fontWeight: 650 }}>
              {state === 'has' ? STR.access.request.youHaveIt : STR.access.request.requested}
            </span>
          )}
        </label>
      );
    });
  }

  const listBox = {
    border: '1px solid var(--border)',
    borderRadius: 10,
    maxHeight: 340,
    overflow: 'auto',
  } as const;

  let picker: ReactNode;
  if (catalogQuery.isLoading) {
    picker = <p className="tx-subtle" style={{ margin: 0, fontSize: 12.5 }}>{STR.common.loading}</p>;
  } else if (catalogQuery.isError) {
    picker = (
      <Callout
        tone="danger"
        role="alert"
        style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
      >
        <span style={{ flex: 1, minWidth: 180 }}>{STR.errors.couldNotLoad}</span>
        <button
          type="button"
          className="tbtn"
          style={quietButtonStyle}
          onClick={() => {
            void catalogQuery.refetch();
          }}
        >
          {STR.common.tryAgain}
        </button>
      </Callout>
    );
  } else if (onOther) {
    picker = (
      <>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            type="button"
            className="ghost"
            style={rowActionStyle}
            onClick={() => {
              setGroupId(null);
            }}
          >
            {STR.access.request.allGroups}
          </button>
          <span style={{ fontSize: 14, fontWeight: 650 }}>{STR.otherRequests.stepTitle}</span>
          <span className="tx-subtle" style={{ fontSize: 12 }}>
            {STR.otherRequests.stepSub}
          </span>
        </div>
        <label>
          <span style={fieldLabelStyle}>{STR.otherRequests.whatLabel}</span>
          <input
            className="field"
            value={other.what}
            placeholder={STR.otherRequests.whatPlaceholder}
            onChange={(event) => {
              setOther({ ...other, what: event.target.value });
            }}
            style={textFieldStyle}
          />
        </label>
        {places.length === 0 ? null : (
          <label>
            <span style={fieldLabelStyle}>{STR.otherRequests.whereLabel}</span>
            <select
              className="field"
              value={other.whereNodeId ?? ''}
              onChange={(event) => {
                setOther({ ...other, whereNodeId: event.target.value === '' ? null : event.target.value });
              }}
              style={textFieldStyle}
            >
              <option value="">{STR.otherRequests.whereUnknown}</option>
              {places.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          <span style={fieldLabelStyle}>{STR.otherRequests.whyLabel}</span>
          <textarea
            className="field"
            rows={3}
            value={other.why}
            placeholder={STR.otherRequests.whyPlaceholder}
            onChange={(event) => {
              setOther({ ...other, why: event.target.value });
            }}
            style={{ ...textFieldStyle, lineHeight: 1.5, resize: 'vertical' }}
          />
        </label>
        {otherIncomplete ? (
          <span className="tx-subtle" style={{ fontSize: 12 }}>
            {STR.otherRequests.whyNeeded}
          </span>
        ) : null}
        <Callout tone="neutral">{STR.otherRequests.stepNote}</Callout>
      </>
    );
  } else if (catalog.length === 0) {
    picker = (
      <>
        <p className="tx-subtle" style={{ margin: 0, fontSize: 12.5 }}>{STR.access.request.emptyCatalog}</p>
        {otherLink}
      </>
    );
  } else if (query !== '') {
    picker = (
      <>
        <div style={listBox}>{rowList(matches)}</div>
        {otherLink}
      </>
    );
  } else if (group === null) {
    picker = (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))',
          gap: 8,
          maxHeight: 340,
          overflow: 'auto',
        }}
      >
        {groups.map((entry) => {
          const picked = entry.rows.filter((item) => picks.has(item.id)).length;
          const available = entry.rows.filter((item) => stateOf(item) === null).length;
          return (
            <button
              key={entry.id}
              type="button"
              className="ghost"
              aria-pressed={picked > 0}
              onClick={() => {
                setGroupId(entry.id);
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 3,
                padding: '11px 12px',
                borderRadius: 10,
                textAlign: 'left',
                cursor: 'pointer',
                ...(picked > 0 ? { borderColor: 'var(--accent)' } : {}),
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 650 }}>{entry.name}</span>
              <span className="tx-subtle" style={{ fontSize: 11.5 }}>
                {STR.access.request.systems(entry.rows.length)}
                {STR.accessCommon.pathSeparator}
                {STR.access.request.available(available)}
              </span>
              {picked > 0 ? (
                <span style={{ fontSize: 11.5, fontWeight: 650, color: 'var(--accent)' }}>
                  {STR.access.request.picked(picked)}
                </span>
              ) : null}
            </button>
          );
        })}
        <button
          type="button"
          className="ghost"
          aria-pressed={hasOther}
          onClick={() => {
            setGroupId(OTHER_GROUP);
          }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 3,
            padding: '11px 12px',
            borderRadius: 10,
            borderStyle: 'dashed',
            textAlign: 'left',
            cursor: 'pointer',
            ...(hasOther ? { borderColor: 'var(--accent)' } : {}),
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 650 }}>{STR.otherRequests.tile}</span>
          <span className="tx-subtle" style={{ fontSize: 11.5 }}>
            {STR.otherRequests.tileSub}
          </span>
          {hasOther ? (
            <span style={{ fontSize: 11.5, fontWeight: 650, color: 'var(--accent)' }}>
              {STR.access.request.picked(1)}
            </span>
          ) : null}
        </button>
      </div>
    );
  } else {
    picker = (
      <>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            type="button"
            className="ghost"
            style={rowActionStyle}
            onClick={() => {
              setGroupId(null);
            }}
          >
            {STR.access.request.allGroups}
          </button>
          <span style={{ fontSize: 14, fontWeight: 650 }}>{group.name}</span>
          <span className="tx-subtle" style={{ fontSize: 12 }}>
            {STR.access.request.systems(group.rows.length)}
          </span>
        </div>
        <div style={listBox}>{rowList(group.rows)}</div>
        {otherLink}
      </>
    );
  }

  const count = pickedItems.length + (hasOther ? 1 : 0);
  const pickedNames = [
    ...pickedItems.map((item) => item.name),
    ...(hasOther ? [STR.otherRequests.pickedOther(otherWhat)] : []),
  ];

  return (
    <Modal
      title={STR.access.request.dialogTitle}
      lead={lead}
      width="min(720px,100%)"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost" style={modalButtonStyle} onClick={onClose}>
            {STR.common.cancel}
          </button>
          <button
            type="button"
            className="prim"
            style={modalButtonStyle}
            disabled={count === 0 || otherIncomplete || sendMutation.isPending}
            onClick={() => {
              const note = reason.trim();
              sendMutation.mutate({
                ids: pickedItems.map((item) => item.id),
                note: note === '' ? undefined : note,
                otherDraft: hasOther ? other : null,
              });
            }}
          >
            {sendMutation.isPending
              ? STR.access.request.sending
              : count > 1
                ? STR.access.request.sendMany(count)
                : STR.access.request.send}
          </button>
        </>
      }
    >
      <div style={{ padding: '16px 22px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {catalog.length === 0 || onOther ? null : (
          <input
            className="field"
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
            placeholder={STR.access.request.searchStep1}
            aria-label={STR.access.request.searchLabel}
            style={searchFieldStyle}
          />
        )}
        {picker}
        {count === 0 ? (
          <div className="tx-subtle" style={{ fontSize: 12.5 }}>
            {STR.access.request.nothingPicked}
          </div>
        ) : (
          <div style={{ fontSize: 12.5 }}>
            <span style={{ fontWeight: 650 }}>{STR.access.request.pickedLine(count)}</span>{' '}
            <span className="tx-muted">{pickedNames.join(', ')}</span>
          </div>
        )}
        {catalog.length === 0 || onOther ? null : (
          <label>
            <span style={fieldLabelStyle}>{STR.access.request.reasonLabel}</span>
            <input
              className="field"
              value={reason}
              maxLength={REQUEST_NOTE_MAX}
              placeholder={STR.access.request.reasonPlaceholder}
              onChange={(event) => {
                setReason(event.target.value);
              }}
              style={textFieldStyle}
            />
          </label>
        )}
      </div>
    </Modal>
  );
}
